//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { useAuthClient } from "../../lib/auth-client";
import { registerLogoutCallback } from "../../lib/auth";
import type { User } from "../../types/auth";

/**
 * Module-level flag: the logout callback is only registered once, regardless
 * of how many times useAuth() is called across components and middleware.
 */
let logoutCallbackRegistered = false;

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Authentication Composable
 *
 * Manages user authentication state and interactions with Keycloak/OIDC provider.
 * Provides centralized authentication logic for the entire application.
 *
 * Responsibilities:
 * - Initialize Keycloak authentication client
 * - Manage user authentication state (logged in/out)
 * - Load and cache user profile information
 * - Handle login/logout workflows with redirects
 * - Provide computed user properties (name, email, initials)
 * - Pass through auth client token methods
 * - Track initialization state to prevent duplicate auth attempts
 */
export const useAuth = () => {
  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const isAuthenticated = useState("auth.isAuthenticated", () => false);
  const user = useState<User | null>("auth.user", () => null);
  const isInitialized = useState("auth.isInitialized", () => false);
  const error = useState("auth.error", () => null);

  const authClient = useAuthClient();

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Get user's display name
   *
   * Purpose:
   * Extract user's preferred display name from profile with fallback chain.
   *
   * @returns Display name string
   */
  const userName = computed(() => {
    return user.value?.name || user.value?.preferred_username || "User Name";
  });

  /**
   * Get user's email address
   *
   * Purpose:
   * Extract user's email from profile with fallback.
   *
   * @returns Email address or placeholder
   */
  const userEmail = computed(() => {
    return user.value?.email || "email@example.com";
  });

  /**
   * Get user's initials for avatar display
   *
   * Purpose:
   * Extract first character of user name for avatar placeholder.
   *
   * @returns Uppercase first character of name
   */
  const userInitials = computed(() => {
    return userName.value.charAt(0).toUpperCase();
  });

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Initialize authentication client
   *
   * Purpose:
   * Bootstrap Keycloak authentication and determine if user is already authenticated.
   * Must be called once during app initialization.
   *
   * Responsibilities:
   * - Prevent duplicate initialization attempts
   * - Configure Keycloak with OIDC endpoints from runtime config
   * - Check existing authentication state (check-sso mode)
   * - Force login if required (login-required mode)
   * - Load user profile if authenticated
   * - Update authentication state
   * - Handle initialization errors gracefully
   *
   * Initialization Modes:
   * - "check-sso": Check if user has active SSO session without forcing login
   * - "login-required": Redirect to login if not authenticated
   *
   * @param onLoad - Initialization mode (default: "check-sso")
   * @returns True if user is authenticated, false otherwise
   */
  const init = async (onLoad: "login-required" | "check-sso" = "check-sso") => {
    // Register the module-level logout callback once.
    // When auth.ts fires logOut() from a timer (inactivity / refresh-token expiry),
    // this callback synchronously resets the Nuxt reactive state so that:
    //  1. Any in-flight SPA navigation correctly redirects to the login page.
    //  2. The auth middleware does not serve stale isAuthenticated=true state
    //     if keycloak.logout() fails to navigate the browser.
    if (!logoutCallbackRegistered) {
      logoutCallbackRegistered = true;
      registerLogoutCallback(() => {
        isAuthenticated.value = false;
        isInitialized.value = false;
        user.value = null;
      });
    }

    if (isInitialized.value) {
      return isAuthenticated.value;
    }

    // One-time cleanup of stale mock sessionStorage data from before the real
    // backend was wired up. Safe to run on every cold init — keys won't exist
    // once the mock store is gone.
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("tests.mock.mappings");
      sessionStorage.removeItem("tests.mock.processors");
      sessionStorage.removeItem("tests.mock.new");
    }

    try {
      error.value = null;
      const config = useRuntimeConfig();
      const authenticated = await authClient.initAuth(onLoad, {
        kcUrl: config.public.OIDC.authority,
        kcRealm: config.public.OIDC.realm,
        kcClientId: config.public.OIDC.clientId,
        autoRefreshEnabled: config.public.OIDC.tokenAutoRefresh,
        autoRefreshCheckInterval: config.public.OIDC.tokenAutoRefreshCheckInterval,
        autoRefreshTimeLeft: config.public.OIDC.tokenAutoRefreshTimeLeft,
        activityTrackingEnabled: config.public.OIDC.tokenAutoRefresh,
      });

      isAuthenticated.value = authenticated;
      isInitialized.value = true;

      if (authenticated) {
        await loadUser();
      }

      return authenticated;
    } catch (err: Error | any) {
      error.value = err;
      console.error("Auth initialization failed:", err);
      return false;
    }
  };

  /**
   * Load user profile information from OIDC provider
   *
   * Purpose:
   * Fetch and cache current user's profile data from Keycloak.
   * Called automatically after successful authentication.
   *
   * Responsibilities:
   * - Fetch user info from OIDC userinfo endpoint
   * - Update user state with profile data
   * - Handle fetch errors gracefully
   */
  const loadUser = async () => {
    try {
      user.value = (await authClient.getUserInfo()) ?? null;
    } catch (error) {
      console.error("Failed to load user:", error);
    }
  };

  /**
   * Initiate login flow
   *
   * Purpose:
   * Redirect user to Keycloak login page.
   * After successful authentication, user is redirected back to application.
   *
   * Responsibilities:
   * - Trigger Keycloak login redirect
   * - Optional: Specify custom redirect URI after login
   * - Handle login errors
   *
   * @param redirectUri - Optional URL to redirect after login
   */
  const login = async (redirectUri?: string) => {
    try {
      const config = useRuntimeConfig();
      const baseUrl = config.public.APP_BASE_URL || "/";
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const fullRedirectUri = redirectUri || `${origin}${baseUrl}`;
      await authClient.logIn(fullRedirectUri);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  /**
   * Logout user and cleanup session
   *
   * Purpose:
   * End user session, cleanup application state, and redirect to Keycloak logout.
   *
   * Responsibilities:
   * - Reset authentication state BEFORE Keycloak logout (prevents race conditions)
   * - Disconnect SignalR connections (currently commented out)
   * - Trigger Keycloak logout with redirect to home page
   * - Force redirect to login on error
   *
   * Critical Note:
   * State must be reset BEFORE authClient.logOut() because Keycloak logout
   * triggers an immediate redirect. If state is reset after, it may not complete
   * before the redirect occurs.
   */
  const logout = async () => {
    try {
      // Reset state BEFORE calling authClient.logOut
      isAuthenticated.value = false;
      user.value = null;
      isInitialized.value = false;

      // Clear any leftover mock sessionStorage keys so stale fake data
      // does not appear after re-login.
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("tests.mock.mappings");
        sessionStorage.removeItem("tests.mock.processors");
        sessionStorage.removeItem("tests.mock.new");
      }

      // Call Keycloak logout - this will redirect to Keycloak and then back
      const config = useRuntimeConfig();
      const baseUrl = config.public.APP_BASE_URL || "/";
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      await authClient.logOut(`${origin}${baseUrl}`);
    } catch (error) {
      console.error("Logout failed:", error);
      const config = useRuntimeConfig();
      const baseUrl = config.public.APP_BASE_URL || "/";
      await navigateTo(baseUrl, { replace: true, external: true });
    }
  };

  /**
   * Refresh user profile information
   *
   * Purpose:
   * Reload user profile from OIDC provider to get latest data.
   * Useful after profile updates or role changes.
   *
   * Responsibilities:
   * - Re-fetch user info from Keycloak
   * - Update cached user state
   */
  const refreshUserInfo = async () => {
    await loadUser();
  };

  return {
    // State
    isAuthenticated: readonly(isAuthenticated),
    user: readonly(user),
    userName: readonly(userName),
    userEmail: readonly(userEmail),
    userInitials: readonly(userInitials),
    error: readonly(error),

    // Auth client passthrough
    getRoles: authClient.getRoles,
    getToken: authClient.getToken,
    refreshToken: authClient.refreshToken,
    accessTokenTimeLeft: authClient.accessTokenTimeLeft,
    refreshTokenTimeLeft: authClient.refreshTokenTimeLeft,

    // Methods
    init,
    login,
    logout,
    refreshUserInfo,
    clearError: () => (error.value = null),
  };
};
